import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Separator } from "@/shared/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/shared/components/ui/dialog";
import { Eye, EyeOff, ArrowLeft, Gift } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/shared/services/api";
import { useDeviceTracking } from "@/shared/hooks/useDeviceTracking";
import { validatePassword, isValidEmail } from "@/lib/validation";

// Scroll to top when component mounts
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

type SignupStep = 'email' | 'password' | 'complete';

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [shakeInputs, setShakeInputs] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(false);

  // OTP-based password reset state
  const [resetStep, setResetStep] = useState<'email' | 'otp' | 'password'>('email');
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [isForgotPasswordDialogOpen, setIsForgotPasswordDialogOpen] = useState(false);

  // Signup flow state
  const [signupStep, setSignupStep] = useState<SignupStep>('email');
  const [emailError, setEmailError] = useState("");

  // Referral tracking - simplified (no API calls during signup)
  const [referralCodeFromUrl, setReferralCodeFromUrl] = useState<string | null>(null);

  const appBaseUrl = import.meta.env.VITE_PUBLIC_BASE_URL || window.location.origin;
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { checkAndNotifyLogin } = useDeviceTracking();

  // Check if user came from landing page (not from logout)
  const fromPage = location.state?.from as string | undefined;
  const fromLandingPage = fromPage === '/pricing' || fromPage === '/' || fromPage?.startsWith('/lp/');

  // Check for mode parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const requestedMode = urlParams.get('mode');

  // Capture referral code from URL on page load - SIMPLIFIED (no validation API call)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Debug: Log all URL parameters
    const allParams: Record<string, string> = {};
    urlParams.forEach((value, key) => {
      allParams[key] = value;
    });
    if (Object.keys(allParams).length > 0) {
      console.log('📋 All URL parameters:', allParams);
    }

    // Check for referral code - support multiple parameter names
    const refCode = urlParams.get('ref') ||
                    urlParams.get('referralCode') ||
                    urlParams.get('referral_code') ||
                    urlParams.get('code');

    if (refCode) {
      console.log('📎 Referral code detected in URL:', refCode);
      setReferralCodeFromUrl(refCode);
      localStorage.setItem('pendingReferralCode', refCode);
      // Switch to signup mode when referral code is detected
      setIsLogin(false);
    } else {
      // Fallback: Check localStorage for referral code from landing page
      const storedRefCode = localStorage.getItem('pendingReferralCode');
      if (storedRefCode) {
        console.log('📎 Referral code detected in localStorage:', storedRefCode);
        setReferralCodeFromUrl(storedRefCode);
        // Switch to signup mode when referral code is detected
        setIsLogin(false);
      } else {
        console.log('ℹ️ No referral code in URL or localStorage');
      }
    }
  }, []);

  // Initialize Google One Tap on page load (only if not already signed in)
  useEffect(() => {
    const WEB_CLIENT_ID = '9332353701-nbub4mr46dd52qa54l2ncrnsf724kpuj.apps.googleusercontent.com';

    const initGoogleOneTap = async () => {
      // ✅ FIX: Don't show One Tap if user is already signed in
      const { hasSession } = await import('@/shared/utils/supabaseStorage');
      if (hasSession()) {
        console.log('[Google One Tap] Skipping - user already signed in');
        return;
      }

      console.log('[Google One Tap] Initializing...');

      if ((window as any).google) {
        console.log('[Google One Tap] Google SDK loaded, configuring...');
        (window as any).google.accounts.id.initialize({
          client_id: WEB_CLIENT_ID,
          callback: async (response: any) => {
            // Don't set loading - AppLoadingGate will show unified loading during auth
            try {
              const idToken = response.credential;
              if (!idToken) throw new Error('No ID token received');

              console.log('One Tap: Received ID token from Google');

              const { error } = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token: idToken,
              });

              if (error) {
                if (error.message?.includes('already in use')) {
                  toast({
                    title: "Account Already Exists",
                    description: "This email is already registered with a password. Please use email login.",
                    variant: "destructive",
                  });
                } else {
                  throw error;
                }
              } else {
                console.log('One Tap: Google sign-in successful');
                setTimeout(() => {
                  checkAndNotifyLogin();
                }, 0);
                // AppLoadingGate will show unified loading during redirect
              }
            } catch (err: any) {
              console.error('One Tap error:', err);
              toast({
                title: "Sign-in Error",
                description: "Failed to sign in with Google. Please try again.",
                variant: "destructive",
              });
            }
          },
        });

        // Prompt for Google One Tap (silently ignore errors)
        try {
          console.log('[Google One Tap] Calling prompt()...');
          (window as any).google.accounts.id.prompt((notification: any) => {
            // Silently handle notification - common errors:
            // - skipped_by_user: User closed the prompt
            // - opt_out_or_no_session: User not logged into Google
            // - browser_not_supported: Browser doesn't support One Tap
            // - suppressed_by_user: User previously dismissed
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              // One Tap not shown - this is expected behavior, not an error
              const reason = notification.getNotDisplayedReason() || notification.getSkippedReason();
              console.log('[Google One Tap] Not displayed. Reason:', reason);
              console.log('[Google One Tap] Full notification:', notification);
            } else if (notification.isDisplayed()) {
              console.log('[Google One Tap] ✅ Displayed successfully');
            }
          });
        } catch (err) {
          // Silently ignore One Tap errors - user can still use manual sign-in button
          console.error('[Google One Tap] Error during prompt():', err);
        }
      } else {
        console.warn('[Google One Tap] Google SDK not loaded yet');
      }
    };

    if ((window as any).google) {
      initGoogleOneTap();
    } else {
      const checkInterval = setInterval(() => {
        if ((window as any).google) {
          initGoogleOneTap();
          clearInterval(checkInterval);
        }
      }, 100);

      setTimeout(() => clearInterval(checkInterval), 5000);
    }
  }, []);

  // Check if user is already logged in and handle password reset
  useEffect(() => {
    scrollToTop();

    if (requestedMode === 'signup') {
      setIsLogin(false);
    } else if (requestedMode === 'login') {
      setIsLogin(true);
    }

    const checkUser = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('reset') === 'true') {
        // CRITICAL FIX: Use localStorage instead of getSession()
        const { hasSession } = await import('@/shared/utils/supabaseStorage');
        if (hasSession()) {
          setIsPasswordReset(true);
        } else {
          toast({
            title: "Password Reset Failed",
            description: "Invalid or expired reset link. Please request a new password reset.",
            variant: "destructive",
          });
          navigate("/auth");
        }
        return;
      }

      // CRITICAL FIX: Use localStorage instead of getSession()
      const { hasSession } = await import('@/shared/utils/supabaseStorage');
      if (hasSession()) {
        navigate("/dashboard");
      }
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const urlParams = new URLSearchParams(window.location.search);
      const isResetFlow = urlParams.get('reset') === 'true';

      if (event === 'PASSWORD_RECOVERY' && isResetFlow) {
        setIsPasswordReset(true);
      } else if (session && !isPasswordReset && !isResetFlow) {
        console.log('Auth state change - checking profile status');
        setCheckingProfile(true);

        setTimeout(() => {
          checkAndNotifyLogin();
        }, 0);

        // Check if profile is complete before redirecting
        // CRITICAL: Always use backend API, never trust metadata cache
        (async () => {
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
              setCheckingProfile(false);
              navigate("/profile-setup");
              return;
            }

            // ALWAYS check backend API for latest profile status (never use metadata)
            // This prevents bypassing profile setup due to stale metadata
            try {
              console.log('[AuthPage] Checking profile status via backend API...');
              const response = await fetch(`${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/api/user/me`, {
                headers: {
                  'Authorization': `Bearer ${session.access_token}`
                }
              });

              if (response.ok) {
                const userData = await response.json();
                const user = userData?.user;
                console.log('[AuthPage] Profile check response:', {
                  profile_setup_complete: user?.profile_setup_complete,
                  hasCompany: !!userData?.company,
                  companyId: userData?.company?.id,
                  hasPersonalDetails: !!(user?.first_name && user?.last_name && user?.phone)
                });

                // Check if profile is TRULY complete (not just auto-created by trigger)
                // Must have: profile_setup_complete=true, company exists, AND personal details filled
                const isProfileComplete = user?.profile_setup_complete &&
                                         userData?.company &&
                                         user?.first_name &&
                                         user?.last_name &&
                                         user?.phone;

                // Chrome extension OAuth: return to /extension-auth so tokens reach the extension
                try {
                  const extReturn = sessionStorage.getItem("wlkthru_extension_oauth_return");
                  if (extReturn && extReturn.startsWith("/extension-auth")) {
                    sessionStorage.removeItem("wlkthru_extension_oauth_return");
                    console.log("[AuthPage] Returning to extension auth flow");
                    setCheckingProfile(false);
                    navigate(extReturn);
                    return;
                  }
                } catch (_) {}

                if (isProfileComplete) {
                  console.log('[AuthPage] ✅ Profile complete - redirecting to dashboard');
                  setCheckingProfile(false);
                  navigate("/dashboard");
                  return;
                } else {
                  console.log('[AuthPage] ❌ Profile incomplete - redirecting to profile-setup');
                  setCheckingProfile(false);
                  navigate("/profile-setup");
                  return;
                }
              } else {
                console.log('[AuthPage] Profile check failed with status:', response.status);
                setCheckingProfile(false);
                navigate("/profile-setup");
                return;
              }
            } catch (error) {
              console.error('[AuthPage] Profile check failed:', error);
              setCheckingProfile(false);
              navigate("/profile-setup");
              return;
            }
          } catch (error) {
            console.error('[AuthPage] Error checking profile:', error);
            setCheckingProfile(false);
            navigate("/profile-setup");
          }
        })();
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isPasswordReset, toast]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // LOGIN FLOW
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            setShakeInputs(true);
            setTimeout(() => setShakeInputs(false), 1000);

            toast({
              title: "Login Failed",
              description: "Invalid email or password. Please check your credentials.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Login Error",
              description: error.message,
              variant: "destructive",
            });
          }
        } else {
          setTimeout(() => {
            checkAndNotifyLogin();
          }, 0);

          setTimeout(async () => {
            try {
              const { data: { user } } = await supabase.auth.getUser();
              const meta = user?.user_metadata;
              const userName = meta?.full_name || meta?.company_name || 'there';
              const today = new Date();
              const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const currentDay = weekdays[today.getDay()];

              toast({
                title: `Welcome back ${userName}, happy ${currentDay}!`,
                description: "You have been successfully logged in.",
              });
            } catch (error) {
              toast({
                title: "Welcome back!",
                description: "You have been successfully logged in.",
              });
            }
          }, 100);
        }
      } else {
        // SIGNUP FLOW - Mobile-like (email → password → profile)
        console.log('Starting signup process, step:', signupStep);

        if (signupStep === 'email') {
          // Step 1: Validate email
          if (!email.trim() || !isValidEmail(email)) {
            toast({
              title: "Invalid Email",
              description: "Please enter a valid email address.",
              variant: "destructive",
            });
            return;
          }

          // SECURITY: Use backend API instead of direct database query
          // Check if email already exists - this is already handled by Supabase auth
          // so we can skip this check as auth.signUp will fail if email exists
          let existingUser = null;
          try {
            const response = await apiClient.get<{ ok: boolean; profile: any }>(`/user/profile?email=${encodeURIComponent(email)}`);
            existingUser = response?.profile;
          } catch (error) {
            // Profile not found is expected for new users
            console.log('No existing profile found (expected for new users)');
          }

          if (existingUser) {
            setEmailError("exists");
            toast({
              title: "Account Already Exists",
              description: "An account with this email already exists. Please try logging in instead.",
              variant: "destructive",
            });
            return;
          }

          setEmailError("");
          setSignupStep('password');
        } else if (signupStep === 'password') {
          // Step 2: Validate passwords
          const passwordError = validatePassword(password);
          if (passwordError) {
            toast({
              title: "Invalid Password",
              description: passwordError,
              variant: "destructive",
            });
            return;
          }

          if (password !== confirmPassword) {
            toast({
              title: "Passwords Don't Match",
              description: "Please ensure both passwords are identical.",
              variant: "destructive",
            });
            return;
          }

          // ✅ FIX: Create account via backend API instead of direct Supabase call
          console.log('Creating account via backend API');

          try {
            const signupResponse = await apiClient.post<{
              ok: boolean;
              user?: { id: string; email: string };
              error?: string;
            }>('/auth/signup', {
              email,
              password,
              metadata: {
                referral_code: referralCodeFromUrl || localStorage.getItem('pendingReferralCode'),
              }
            });

            if (!signupResponse.ok || !signupResponse.user) {
              throw new Error(signupResponse.error || 'Failed to create account');
            }

            console.log('Account created successfully via backend, now signing in...');

            // Sign in to get session tokens
            const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
              email,
              password
            });

            if (authError) {
              console.error('Auto-login after signup failed:', authError);
              toast({
                title: "Account Created",
                description: "Please log in with your new account.",
              });
              setIsLogin(true);
              setSignupStep('email');
              return;
            }

            console.log('Auto-login successful, redirecting to profile setup');

            toast({
              title: "Account Created!",
              description: "Please complete your profile to continue.",
            });

            // Redirect will happen via auth state change listener
          } catch (signupError: any) {
            console.error('Signup error:', signupError);

            if (signupError.message?.includes('already registered') || signupError.error?.includes('already registered')) {
              toast({
                title: "Account Already Exists",
                description: "An account with this email already exists. Please try logging in instead.",
                variant: "destructive",
              });
              setIsLogin(true);
              setSignupStep('email');
            } else {
              toast({
                title: "Signup Error",
                description: signupError.message || signupError.error || 'Failed to create account',
                variant: "destructive",
              });
            }
            return;
          }
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    // Don't set loading - the redirect happens immediately and AppLoadingGate will show unified loading
    try {
      const WEB_CLIENT_ID = '9332353701-nbub4mr46dd52qa54l2ncrnsf724kpuj.apps.googleusercontent.com';
      const REDIRECT_URI = `${window.location.origin}/auth/callback`;

      // ✅ Direct Google OAuth 2.0 flow - shows "Sign in to wlkthru.co"
      // Build OAuth URL
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.append('client_id', WEB_CLIENT_ID);
      authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', 'openid email profile');
      authUrl.searchParams.append('access_type', 'offline');
      authUrl.searchParams.append('prompt', 'select_account');

      // Store current path for redirect after auth
      localStorage.setItem('preAuthPath', window.location.pathname);

      console.log('[Google OAuth] Redirecting to:', authUrl.toString());

      // Redirect to Google OAuth - AppLoadingGate will handle loading state
      window.location.href = authUrl.toString();
    } catch (error: any) {
      console.error("Google auth error:", error);
      toast({
        title: "Error",
        description: "Failed to sign in with Google. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSendOTP = async () => {
    if (!forgotPasswordEmail) {
      toast({
        title: "Email Required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    if (!isValidEmail(forgotPasswordEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setForgotPasswordLoading(true);
    try {
      // SECURITY: Use backend proxy instead of calling edge function directly
      const data = await apiClient.post<{ success: boolean; code?: string; message?: string }>('/auth/send-password-reset-otp', {
        email: forgotPasswordEmail.trim(),
      });

      if (!data?.success) {
        // Handle special error codes
        if (data.code === 'GOOGLE_ACCOUNT') {
          toast({
            title: "Use Google Sign-In",
            description: "This email is linked to a Google account. Please use 'Continue with Google' instead.",
            variant: "destructive",
          });
          return;
        }

        if (data.code === 'NO_USER') {
          toast({
            title: "No Account Found",
            description: "We couldn't find an account with that email. Please check the spelling or sign up.",
            variant: "destructive",
          });
          return;
        }

        throw new Error(data.message || 'Failed to send OTP');
      }

      // Success - move to OTP verification step
      setResetStep('otp');
      toast({
        title: "Code Sent!",
        description: "Please check your email for the 6-digit verification code.",
      });
    } catch (error: any) {
      console.error('Send OTP error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter the 6-digit code from your email.",
        variant: "destructive",
      });
      return;
    }

    if (!newPassword || !confirmNewPassword) {
      toast({
        title: "Password Required",
        description: "Please enter and confirm your new password.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please ensure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      toast({
        title: "Invalid Password",
        description: passwordError,
        variant: "destructive",
      });
      return;
    }

    setForgotPasswordLoading(true);
    try {
      // Step 1: Verify OTP and get reset token
      const verifyData = await apiClient.post<{ ok: boolean; verified?: boolean; resetToken?: string; error?: string }>('/auth/verify-password-reset-otp', {
        email: forgotPasswordEmail.trim(),
        otp: otpCode.trim(),
      });

      if (!verifyData?.ok || !verifyData?.verified || !verifyData?.resetToken) {
        throw new Error(verifyData?.error || 'Failed to verify code');
      }

      // Step 2: Reset password with the token
      const data = await apiClient.post<{ ok: boolean; success?: boolean; code?: string; message?: string }>('/auth/reset-password', {
        resetToken: verifyData.resetToken,
        newPassword: newPassword,
      });

      if (!data?.ok) {
        toast({
          title: "Error",
          description: data.message || 'Failed to reset password',
          variant: "destructive",
        });
        return;
      }

      // Success!
      toast({
        title: "Password Reset Successful",
        description: "Your password has been updated. You can now log in with your new password.",
      });

      // Reset dialog state and close
      setResetStep('email');
      setForgotPasswordEmail("");
      setOtpCode("");
      setNewPassword("");
      setConfirmNewPassword("");
      setIsForgotPasswordDialogOpen(false);

    } catch (error: any) {
      console.error('Verify OTP error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please ensure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      toast({
        title: "Invalid Password",
        description: passwordError,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        if (error.message.includes("same as the old one") ||
            error.message.includes("recently used") ||
            error.message.includes("previous password")) {
          toast({
            title: "Password Reset Failed",
            description: "Please type in a password that hasn't already been used.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Password Reset Failed",
            description: error.message,
            variant: "destructive",
          });
        }
      } else {
        await supabase.auth.signOut();

        toast({
          title: "Password Updated!",
          description: "Your password has been successfully updated. Please sign in with your new password.",
        });
        window.history.replaceState({}, document.title, "/auth");
        setIsPasswordReset(false);
        setIsLogin(true);
        setPassword("");
        setConfirmPassword("");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setSignupStep('email');
    setPassword("");
    setConfirmPassword("");
  };

  // Password Reset Form
  if (isPasswordReset) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-4xl font-bold text-primary mb-2">
              <button
                onClick={() => {
                  window.history.replaceState({}, document.title, "/auth");
                  setIsPasswordReset(false);
                  setIsLogin(true);
                }}
                className="hover:underline"
              >
                wlkthru
              </button>
            </h1>
            <h2 className="text-2xl font-semibold">Reset Your Password</h2>
            <p className="text-muted-foreground">Enter your new password below</p>
          </div>

          <Card className="border-0 shadow-none">
            <CardContent className="pt-6 space-y-6">
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      minLength={8}
                      className="pr-10"
                      spellCheck={false}
                      autoCorrect="off"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      minLength={8}
                      className="pr-10"
                      spellCheck={false}
                      autoCorrect="off"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                  {password && confirmPassword && password === confirmPassword && (
                    <p className="text-sm text-green-600 text-center">Passwords match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Password"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-xs text-muted-foreground">
            © 2025 wlkthru
          </div>
        </div>
      </div>
    );
  }

  // Signup Form - Mobile-like flow
  if (!isLogin) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <div className="flex-1 flex items-center justify-center p-8 relative">
          <Button
            variant="ghost"
            onClick={() => {
              if (signupStep === 'password') {
                handleBackToEmail();
              } else {
                setIsLogin(true);
                setSignupStep('email');
              }
            }}
            className="absolute top-4 left-8 text-gray-600 hover:text-gray-800"
          >
            ← Back
          </Button>

          <div className="w-full max-w-md space-y-8">
            <div className="space-y-2 text-center">
              <h1 className="text-3xl font-bold text-primary mb-2">
                <button
                  onClick={() => navigate('/')}
                  className="hover:underline"
                >
                  wlkthru
                </button>
              </h1>
              <h2 className="text-2xl font-semibold text-gray-900">
                {signupStep === 'email' ? 'Create your account' : 'Set your password'}
              </h2>
              {signupStep === 'email' && (
                <div className="text-gray-600 space-y-1">
                  <p className="leading-tight">15 day free trial</p>
                  <p className="text-sm leading-tight">Then $99/month</p>
                </div>
              )}
            </div>

            {/* Referral Banner - Simplified (no referrer name lookup) */}
            {referralCodeFromUrl && signupStep === 'email' && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-lg shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                    <Gift className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-blue-900 font-bold text-lg">
                      You've been referred!
                    </p>
                    <p className="text-blue-800 mt-1">
                      Get <span className="font-semibold">$100 off</span> your first 3 months
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleEmailAuth} className="space-y-4">
              {signupStep === 'email' && (
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    required
                    disabled={loading}
                    className={emailError === "exists" ? "border-red-500 focus:border-red-500" : ""}
                  />
                  {emailError === "exists" && (
                    <p className="text-sm text-red-500">
                      It looks like an account with this email already exists, try{" "}
                      <button
                        type="button"
                        onClick={() => setIsLogin(true)}
                        className="text-primary hover:underline font-medium"
                      >
                        logging in instead?
                      </button>
                    </p>
                  )}
                </div>
              )}

              {signupStep === 'password' && (
                <>
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password (min 8 chars, 1 uppercase, 1 symbol)"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                        minLength={8}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={loading}
                        minLength={8}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    </div>
                    {password && confirmPassword && password === confirmPassword && (
                      <p className="text-sm text-green-600 text-center">Passwords match</p>
                    )}
                  </div>
                </>
              )}

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 h-12"
                disabled={loading}
              >
                {loading ? "Please wait..." : signupStep === 'email' ? "Continue" : "Create Account"}
              </Button>
            </form>

            {signupStep === 'email' && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-gray-100 px-2 text-muted-foreground">Or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleAuth}
                  disabled={loading}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Sign up with Google
                </Button>
              </>
            )}

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-xs text-gray-500">
              © 2025 wlkthru
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't show loading screen here - let AppLoadingGate handle it
  // This prevents double loading screens (small box then full overlay)
  if (checkingProfile) {
    return null; // ✅ Removed loading UI, AppLoadingGate will show unified loading
  }

  // Login Form
  return (
    <div className="min-h-screen bg-background flex">
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 relative">
        <div className="w-full max-w-md space-y-6 md:space-y-8">
          {fromLandingPage && (
            <div className="flex justify-start md:absolute md:top-8 md:left-8">
              <Button
                variant="ghost"
                onClick={() => navigate(fromPage || '/')}
                className="text-muted-foreground hover:text-foreground p-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </div>
          )}

          <div className="text-center space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
              <button
                onClick={() => navigate('/')}
                className="hover:underline"
              >
                wlkthru
              </button>
            </h1>
            <h2 className="text-2xl font-semibold">Welcome to wlkthru</h2>
            <p className="text-muted-foreground">Login or try 15 days free</p>
          </div>

          <Card className="border-0 shadow-none">
            <CardContent className="pt-6 space-y-6">
              <form onSubmit={handleEmailAuth} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                   <Input
                     id="email"
                     type="email"
                     placeholder="Enter your Email Address"
                     value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     required
                     disabled={loading}
                     spellCheck={false}
                     autoCorrect="off"
                     className={shakeInputs ? "shake" : ""}
                   />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      minLength={6}
                       className={`pr-10 ${shakeInputs ? "shake" : ""}`}
                      spellCheck={false}
                      autoCorrect="off"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                >
                  {loading ? "Please wait..." : "Sign In"}
                </Button>
              </form>

              <Dialog open={isForgotPasswordDialogOpen} onOpenChange={(open) => {
                setIsForgotPasswordDialogOpen(open);
                if (!open) {
                  // Reset state when dialog closes
                  setResetStep('email');
                  setForgotPasswordEmail("");
                  setOtpCode("");
                  setNewPassword("");
                  setConfirmNewPassword("");
                }
              }}>
                <DialogTrigger asChild>
                  <Button variant="ghost" className="w-full text-sm text-muted-foreground hover:text-primary">
                    Forgot Password?
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>
                      {resetStep === 'email' && 'Reset Password'}
                      {resetStep === 'otp' && 'Enter Verification Code'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {/* Step 1: Enter Email */}
                    {resetStep === 'email' && (
                      <>
                        <div>
                          <Label htmlFor="reset-email">Email Address</Label>
                          <Input
                            id="reset-email"
                            type="email"
                            placeholder="Enter your email address"
                            value={forgotPasswordEmail}
                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSendOTP();
                              }
                            }}
                            disabled={forgotPasswordLoading}
                            spellCheck={false}
                            autoCorrect="off"
                          />
                        </div>
                        <Button
                          onClick={handleSendOTP}
                          className="w-full"
                          disabled={forgotPasswordLoading}
                        >
                          {forgotPasswordLoading ? "Sending..." : "Send Verification Code"}
                        </Button>
                      </>
                    )}

                    {/* Step 2: Enter OTP and New Password */}
                    {resetStep === 'otp' && (
                      <>
                        <div className="text-sm text-muted-foreground">
                          We sent a 6-digit code to <span className="font-medium text-foreground">{forgotPasswordEmail}</span>
                        </div>
                        <div>
                          <Label htmlFor="otp-code">Verification Code</Label>
                          <Input
                            id="otp-code"
                            type="text"
                            placeholder="Enter 6-digit code"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            maxLength={6}
                            disabled={forgotPasswordLoading}
                          />
                        </div>
                        <div>
                          <Label htmlFor="new-password">New Password</Label>
                          <div className="relative">
                            <Input
                              id="new-password"
                              type={showNewPassword ? "text" : "password"}
                              placeholder="Enter new password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              disabled={forgotPasswordLoading}
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                          <div className="relative">
                            <Input
                              id="confirm-new-password"
                              type={showConfirmNewPassword ? "text" : "password"}
                              placeholder="Confirm new password"
                              value={confirmNewPassword}
                              onChange={(e) => setConfirmNewPassword(e.target.value)}
                              disabled={forgotPasswordLoading}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleVerifyOTP();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showConfirmNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setResetStep('email');
                              setOtpCode("");
                              setNewPassword("");
                              setConfirmNewPassword("");
                            }}
                            className="w-full"
                            disabled={forgotPasswordLoading}
                          >
                            Back
                          </Button>
                          <Button
                            onClick={handleVerifyOTP}
                            className="w-full"
                            disabled={forgotPasswordLoading}
                          >
                            {forgotPasswordLoading ? "Resetting..." : "Reset Password"}
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          onClick={handleSendOTP}
                          className="w-full text-sm"
                          disabled={forgotPasswordLoading}
                        >
                          Resend Code
                        </Button>
                      </>
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleAuth}
                disabled={loading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </Button>

              <Button
                className="w-full bg-primary hover:bg-primary/90"
                onClick={() => {
                  setEmail("");
                  setPassword("");
                  setConfirmPassword("");
                  setEmailError("");
                  setSignupStep('email');
                  setIsLogin(false);
                }}
              >
                Need an account? Sign up
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                © 2025 wlkthru
              </p>

              <p className="text-center text-xs text-muted-foreground">
                By proceeding, you agree to the{" "}
                <a href="/legal?section=terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Terms of Service
                </a>{" "}
                &{" "}
                <a href="/legal?section=privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Privacy Policy
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
}
